import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useMediaQuery } from '@mui/material';
import { Theme, useTheme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import _debounce from 'lodash/debounce';
import { hashHistory } from 'react-router';
import { Waypoint } from 'react-waypoint';

import { NAV_BAR_HEIGHT } from '../../../pages/RestaurantMenu/RestaurantMenu';
import { MenuActionsType } from '../../../types/menu';
import { changeUtils, locationUtils, menuUtils } from '../../../utils';
import { usePrevious, useScroll } from '../../../utils/hooks';
import * as Api from '../../../weborder/Api';
import * as BasketControl from '../../../weborder/BasketControl';
import { Routes } from '../../../weborder/Routes';
import { Utilities } from '../../../weborder/Utilities';
import { Snackbar } from '../../atoms';
import { LoadingSpinner } from '../../layouts';
import { menuSectionUtils } from './menuSectionUtils';
import { MenuNavigation, MenuSection } from './partials';

interface Props {
  actions: MenuActionsType;
  basket: BasketControl.Data;
  configData: Api.AppConfig;
  currency: Api.Currency;
  expandedState: number[];
  isStaticMenu?: boolean;
  languageCode: string;
  menuData: Api.MenuReposnse | null;
  params?: Routes['params'];
  restaurantData: Api.CollectionRestaurantDetail | Api.DeliveryRestaurantDetail;
  translate: any;
  viewportTopOffset: number;
}

const useStyles = () =>
  makeStyles((theme: Theme) => ({
    sections: {
      position: 'relative',
      zIndex: 0,
    },

    [theme.breakpoints.up('sm')]: {
      message: {
        margin: theme.spacing(0, 3),
      },

      navigation: {
        '& .mobileNav': {
          padding: theme.spacing(0, 3),
        },
      },
    },

    [theme.breakpoints.up('md')]: {
      message: {
        margin: theme.spacing(0),
      },
    },
  }))();

const ACTIVATION_OFFSET_TRIGGER_HEIGHT = 100;
const MOBILE_OFFSET_TOP_FOR_MENU_SECTIONS = 121;

export function Menu(props: Props): JSX.Element {
  const {
    actions,
    basket,
    configData,
    currency,
    expandedState,
    isStaticMenu = false,
    languageCode,
    menuData,
    params,
    restaurantData,
    translate,
    viewportTopOffset,
  } = props;

  const concessionStoreId = Number(params.concessionStoreId);
  const vrId = Number(params.vrid);

  const { getAvailableMenuSections, getConcessionStoreAvailability } =
    menuSectionUtils;

  const setFirstMenuSectionAsActive = (availableMenus): void => {
    const [firstMenuSection] = availableMenus;
    setActiveSection(firstMenuSection.MenuSectionId);
  };

  const [activeSection, setActiveSection] = useState(undefined);

  const theme = useTheme();
  const classes = useStyles();

  const { scrollOnTop } = useScroll();
  const hasCompactMenu = useMediaQuery(theme?.breakpoints.down('sm'));

  const hasConcessionStores = menuData?.ConcessionStores.length > 0;
  const navBarHeight = hasConcessionStores
    ? MOBILE_OFFSET_TOP_FOR_MENU_SECTIONS
    : NAV_BAR_HEIGHT;
  const firstMenuOptionHeight = hasCompactMenu ? navBarHeight : 0;

  const previousConcessionId = usePrevious(params?.concessionStoreId);
  const hasNewConcessionStore =
    hasConcessionStores &&
    previousConcessionId !== undefined &&
    previousConcessionId !== params?.concessionStoreId;

  const availableMenuSections = useMemo(() => {
    if (!menuData) {
      return [];
    }

    let availableSections = getAvailableMenuSections(
      menuData.MenuSections,
      restaurantData.TimeZoneInfoId,
      concessionStoreId,
    );
    availableSections = changeUtils.sortBy(availableSections, 'DisplayOrder');

    if (availableSections.length > 0) {
      setFirstMenuSectionAsActive(availableSections);
    }

    return availableSections;
  }, [menuData?.MenuId, concessionStoreId]);

  useEffect(() => {
    if (menuData) {
      setupExpandedStates();
    }
  }, [menuData?.MenuId]);

  useEffect(() => {
    if (
      scrollOnTop &&
      activeSection &&
      availableMenuSections.length &&
      activeSection !== availableMenuSections[0].MenuSectionId
    ) {
      setActiveSection(availableMenuSections[0].MenuSectionId);
    }
  }, [scrollOnTop, activeSection, availableMenuSections.length]);

  useEffect(() => {
    if (menuData?.MenuId && params?.concessionStoreId) {
      const isConcessionStoreAvailable = getConcessionStoreAvailability(
        params?.concessionStoreId,
        menuData?.MenuSections,
        restaurantData?.TimeZoneInfoId,
      );

      if (!isConcessionStoreAvailable) {
        hashHistory.replace(
          `/restaurant/${params.vrid}/${params.type}/${params.id}`,
        );
      }
    }
  }, [menuData?.MenuId, params?.concessionStoreId]);

  useEffect(() => {
    const scrollPageToConcessionMenus = (anchorPosition): void => {
      _debounce(() => {
        window.scrollTo({
          behavior: 'smooth',
          top: anchorPosition,
        });
      }, 300)();
    };

    const setFirstMenuSectionActive = (newTopSectionId): void => {
      _debounce(() => {
        setActiveSection(newTopSectionId);
      }, 1000)();
    };

    if (hasNewConcessionStore) {
      const concessionAnchorElement =
        document.getElementById('concessionAnchor');
      const anchorPosition =
        concessionAnchorElement.getBoundingClientRect().top + window.scrollY;
      scrollPageToConcessionMenus(anchorPosition);

      const newTopSectionId = availableMenuSections[0]?.MenuSectionId;
      setFirstMenuSectionActive(newTopSectionId);
    }
  }, [params?.concessionStoreId]);

  const checkSectionAvailabilityMode = (menuSection): boolean => {
    const {
      DisplayAlways,
      DisplayBasedOnTimes,
      DisplayAlwaysStartCollapsedBasedOnTimes,
    } = Api.MenuEntityAvailabilityMode;
    const {
      AvailabilityMode: availabilityMode,
      AvailableTimes: availabilityTimes,
    } = menuSection.MenuSectionAvailability;
    const timezone = restaurantData.TimeZoneInfoId;

    switch (availabilityMode) {
      case DisplayAlways:
        return true;

      case DisplayBasedOnTimes: {
        return Utilities.isInPeriod(availabilityTimes, timezone);
      }

      case DisplayAlwaysStartCollapsedBasedOnTimes: {
        return Utilities.isInPeriod(availabilityTimes, timezone);
      }

      default:
        return false;
    }
  };

  const setupExpandedStates = (): void => {
    let expandedSections = [];

    menuData.MenuSections.forEach((menuSection) => {
      if (
        !menuSection.MenuSectionAvailability ||
        checkSectionAvailabilityMode(menuSection)
      ) {
        expandedSections.push(menuSection.MenuSectionId);
      }
    });

    if (
      menuData.MenuSectionBehaviour === Api.MenuSectionBehaviour.ExpandSingle
    ) {
      expandedSections = expandedSections.slice(0, 1);
    }

    actions.initExpanded(vrId, expandedSections);
  };

  const handleTabClick = (sectionId: number): void => {
    const isExpandSingle =
      menuData?.MenuSectionBehaviour === Api.MenuSectionBehaviour.ExpandSingle;

    if (isExpandSingle) {
      actions.initExpanded(vrId, [sectionId]);
    } else {
      actions.onAddExpanded(vrId, sectionId);
    }

    const targetId = `menuSection-${sectionId.toString()}`;
    _debounce(() => {
      menuUtils.handleScrollToSection(targetId, hasCompactMenu, navBarHeight);
      setActiveMenuDebounced(sectionId);
    }, 300)();
  };

  const setActiveMenuDebounced = useCallback(
    _debounce((sectionId) => {
      setActiveSection(sectionId);
    }, 600),
    [],
  );

  if (!menuData || !activeSection) {
    return <LoadingSpinner variant="panel" />;
  }

  const onMenuWaypointEnter = (menuSectionId, event, isFirst): void => {
    const sectionOffset =
      (isFirst ? firstMenuOptionHeight : navBarHeight) +
      viewportTopOffset +
      ACTIVATION_OFFSET_TRIGGER_HEIGHT;

    if (
      event.previousPosition !== 'above' ||
      event.waypointBottom > sectionOffset
    ) {
      return;
    }
    setActiveSection(menuSectionId);
  };

  const onMenuWaypointEnd = (menuSectionId, event, isFirst): void => {
    const sectionOffset =
      (isFirst ? firstMenuOptionHeight : navBarHeight) + viewportTopOffset;
    if (
      event.currentPosition === 'below' ||
      (event.waypointBottom > sectionOffset && event.waypointBottom < 0)
    ) {
      return;
    }
    setActiveSection(menuSectionId);
  };

  const renderMenuHeader = (): JSX.Element => {
    const displayMenuSectionNav = !!menuData.DisplaySectionLinks;

    return (
      <>
        {restaurantData.MenuMessage && (
          <div className={classes.message}>{renderMenuMessage()}</div>
        )}

        <MenuNavigation
          activeMenuSectionId={activeSection}
          availableMenuSections={availableMenuSections}
          menu={menuData}
          onTabClick={handleTabClick}
          params={params}
          showSectionLinks={displayMenuSectionNav}
          timezone={restaurantData.TimeZoneInfoId}
          translate={translate}
          viewportTopOffset={viewportTopOffset}
        />
      </>
    );
  };

  const renderMenuMessage = (): JSX.Element => {
    const menuMessageWithLineBreaks = Utilities.stringToJSX(
      restaurantData.MenuMessage.split('\n'),
      (part: string, index: number) => [part, <br key={`br-${index}`} />],
    );
    menuMessageWithLineBreaks.pop();

    return <Snackbar message={menuMessageWithLineBreaks} variant="info" />;
  };

  const renderMenuSections = (): JSX.Element[] => {
    const getExpandedState = (sectionId: number): boolean => {
      if (typeof expandedState === 'undefined') {
        return false;
      }

      return expandedState.some((section) => section === sectionId);
    };

    const menuSections: JSX.Element[] = availableMenuSections.map(
      (menuSection, index) => {
        const menuSectionData = {
          description: menuSection.Description,
          expandBehaviour: menuData.MenuSectionBehaviour,
          imageUrl: menuSection.ImageUrl,
          items: menuSection.MenuItems,
          name: menuSection.Name,
          sectionId: menuSection.MenuSectionId,
        };
        const isFirst = index === 0;
        const menuSectionId = menuSection.MenuSectionId;
        const isExpanded = getExpandedState(menuSectionId);

        const waypoint = (
          <Waypoint
            onEnter={(event): void =>
              onMenuWaypointEnter(menuSectionId, event, isFirst)
            }
            onLeave={(event): void =>
              onMenuWaypointEnd(menuSectionId, event, isFirst)
            }
            topOffset={viewportTopOffset + navBarHeight}
          />
        );

        const navBarHeightMenuSectionProp = menuData.DisplaySectionLinks
          ? navBarHeight
          : undefined;

        return (
          <MenuSection
            actions={actions}
            basket={basket}
            currency={currency}
            isExpanded={isExpanded}
            key={`menu-section-${menuSectionId}`}
            languageCode={languageCode}
            menuId={menuData.MenuId}
            menuSectionData={menuSectionData}
            navBarHeight={navBarHeightMenuSectionProp}
            translate={translate}
            vrId={vrId}
            waypoint={waypoint}
          />
        );
      },
    );

    return menuSections;
  };

  const isUKStore = locationUtils.checkCountryCode(configData, 'GB');

  const isMenuDisplayReady =
    (hasConcessionStores && concessionStoreId) || !hasConcessionStores;

  return (
    <div className={classes.menu} id="concessionAnchor">
      {!isStaticMenu && renderMenuHeader()}

      {isMenuDisplayReady && (
        <div className={classes.sections} data-fd="menu-content" role="tablist">
          {renderMenuSections()}
        </div>
      )}

      {isUKStore && (
        <Snackbar
          className={classes.calorieAdvice}
          message={translate('Menu_calories_info')}
          variant="info"
        />
      )}
    </div>
  );
}
